import { Box, Grid, Typography } from '@material-ui/core';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import BackButton from '../../../../../utils/backButton';
import { HelpComponent } from '../../../../../utils/helpComponent';
import HELP_URL from '../../../../../utils/helpLinkConstant';
import { useProjectsState } from '../../hooks/useProjectsState';
import EnhanceTestPlanModal from '../ProjectCard/EnhanceTestPlanModal';
import ProjectInfoSummary from './ProjectInfoCard';
import TestExecutionEmptyState from './TestExecutionEmptyState';
import TestExecutionHistory from './TestExecutionHistory';
import TestRunSummary from './TestRunSummary';
import { useStyles } from '../../styles';
import {
  getProjectExecutionActionState,
  type TestExecutionActionKey,
} from '../../utils/projectExecutionActions';
import '../../styles/projectCard.css';
import '../../styles/projectDetails.css';

export const IqeProjectDetails = ({
  isToolAdmin,
}: {
  isToolAdmin: boolean;
}) => {
  const { iqeProjectId, projectId } = useParams();
  const navigate = useNavigate();
  const { getProjectById } = useProjectsState(projectId!);
  const classes = useStyles();
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const project = projectId ? getProjectById(iqeProjectId!) : null;

  if (!project) return null;

  const hasTestAssets = !!(project.runs && project.runs.length > 0);
  const isExecutionInProgress = [
    'GENERATING',
    'EXECUTING',
    'ENHANCING',
  ].includes(project.status);
  const { displayVersion, options } = getProjectExecutionActionState(
    project?.runs,
    isExecutionInProgress,
  );
  const executionHistory = displayVersion ? project.runs : [];

  console.log(
    '🎯 ProjectDetails - project.runs:',
    project.runs,
    'executionHistory:',
    executionHistory,
  );

  const handleGenerateTests = (id: string) => {
    navigate('../', {
      state: {
        triggerExecution: {
          projectId: id,
          projectName: project.name,
          mode: 'GENERATE' as const,
        },
      },
    });
  };

  const handleRunTests = (id: string) => {
    navigate('../', {
      state: {
        triggerExecution: {
          projectId: id,
          projectName: project.name,
          mode: 'RUN' as const,
        },
      },
    });
  };

  const handleActionSelect = (action: TestExecutionActionKey) => {
    switch (action) {
      case 'GENERATE':
        handleGenerateTests(project.id);
        break;
      case 'RUN':
        handleRunTests(project.id);
        break;
      case 'ENHANCE':
        setShowEnhanceModal(true);
        break;
      default:
        break;
    }
  };

  const handleViewResults = (id: string) => {
    navigate('../', {
      state: {
        openResultsProjectId: id,
      },
    });
  };

  return (
    <main>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <BackButton />
        <HelpComponent helpUrl={HELP_URL.PERFORMANCE_TEST_RUNS_PAGE} />
      </Grid>
      <Box className={`pd-page ${classes.projectDetailsPage}`}>
        {/* Header */}
        <Box component="header">
          <Typography variant="h5" className={classes.projectsHeaderTitle}>
            Project Details
          </Typography>
        </Box>

        {/* Project Info Card */}
        <ProjectInfoSummary project={project} currentVersion={displayVersion} />

        {/* Latest Run Summary */}
        {hasTestAssets && (
          <TestRunSummary
            currentVersion={displayVersion}
            actionOptions={options}
            actionDisabled={!isToolAdmin}
            onActionSelect={handleActionSelect}
          />
        )}

        {/* Content Section */}
        {!hasTestAssets ? (
          <TestExecutionEmptyState
            isToolAdmin={isToolAdmin}
            project={project}
            onGenerateTests={handleGenerateTests}
          />
        ) : (
          <TestExecutionHistory
            project={project}
            runs={executionHistory}
            onViewResults={handleViewResults}
          />
        )}

        <EnhanceTestPlanModal
          isToolAdmin={isToolAdmin}
          open={showEnhanceModal}
          onClose={() => setShowEnhanceModal(false)}
          onRunTests={handleRunTests}
          project={project}
        />
      </Box>
    </main>
  );
};
